/**
 * AIAmbassadorPanel — collapsible "AI Research Ambassador" widget.
 *
 * Two modes:
 *   mode="search"       → community-wide question answering (used on
 *                         Community home + Category pages). Sends the
 *                         question to /api/forum/ai-ambassador/ask and
 *                         renders the answer with clickable citations.
 *   mode="fact-check"   → thread-level fact check (used on CommunityTopic).
 *                         Calls /api/forum/ai-ambassador/fact-check/:id
 *                         and renders the structured verdict card.
 */
import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import AICostBadge from '../AICostBadge';
import {
  Sparkles, Bot, AlertTriangle, CheckCircle2, HelpCircle, ShieldAlert,
  RefreshCw, ChevronRight, MessageSquare, Lock, Zap, Crown, TrendingUp,
} from 'lucide-react';

// Human-friendly labels + upsell colour for each subscription tier.
const TIER_META = {
  free:       { label: 'Free',       Icon: Sparkles,    color: 'text-slate-600 dark:text-slate-300', bg: 'bg-slate-500/10 border-slate-500/25' },
  pro:        { label: 'Pro',        Icon: Zap,         color: 'text-blue-600',   bg: 'bg-blue-500/10 border-blue-500/25' },
  business:   { label: 'Business',   Icon: TrendingUp,  color: 'text-emerald-600', bg: 'bg-emerald-500/10 border-emerald-500/25' },
  enterprise: { label: 'Enterprise', Icon: Crown,       color: 'text-amber-600',  bg: 'bg-amber-500/10 border-amber-500/25' },
};

const VERDICT_META = {
  accurate: { color: 'text-emerald-600', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', Icon: CheckCircle2, label: 'Accurate' },
  partially_accurate: { color: 'text-amber-600', bg: 'bg-amber-500/10', border: 'border-amber-500/30', Icon: AlertTriangle, label: 'Partially accurate' },
  misleading: { color: 'text-rose-600', bg: 'bg-rose-500/10', border: 'border-rose-500/30', Icon: ShieldAlert, label: 'Misleading' },
  unverified: { color: 'text-slate-600', bg: 'bg-slate-500/10', border: 'border-slate-500/30', Icon: HelpCircle, label: 'Unverified' },
};

// Turn `[title](/community/t/<id>)` markdown links into inline React Router
// links, and `**bold**` into <strong>. Any external `[title](https://...)`
// also becomes a real anchor. Keeps the ambassador answers looking clean
// instead of dumping raw markdown on screen.
const MD_LINK_RE = /\[([^\]]+)\]\((\/community\/t\/[a-f0-9-]+|https?:\/\/[^\s)]+)\)/g;
const BOLD_RE = /\*\*([^*]+)\*\*/g;

const renderInlineBold = (str, keyPrefix) => {
  const nodes = [];
  let last = 0;
  let m;
  BOLD_RE.lastIndex = 0;
  while ((m = BOLD_RE.exec(str)) !== null) {
    if (m.index > last) nodes.push(str.slice(last, m.index));
    nodes.push(<strong key={`${keyPrefix}-b-${m.index}`} className="font-bold text-foreground">{m[1]}</strong>);
    last = m.index + m[0].length;
  }
  if (last < str.length) nodes.push(str.slice(last));
  return nodes;
};

const renderAnswerText = (text) => {
  if (!text) return null;
  const parts = [];
  let last = 0;
  let m;
  MD_LINK_RE.lastIndex = 0;
  while ((m = MD_LINK_RE.exec(text)) !== null) {
    if (m.index > last) parts.push({ type: 'text', value: text.slice(last, m.index) });
    parts.push({ type: 'link', label: m[1], href: m[2] });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ type: 'text', value: text.slice(last) });
  return parts.map((p, i) => {
    if (p.type === 'text') return <React.Fragment key={i}>{renderInlineBold(p.value, `t${i}`)}</React.Fragment>;
    const internal = p.href.startsWith('/community/');
    if (internal) {
      return (
        <Link
          key={i}
          to={p.href}
          data-testid="ambassador-inline-link"
          className="font-bold text-primary underline decoration-primary/40 hover:decoration-primary underline-offset-2"
        >
          {p.label}
        </Link>
      );
    }
    return (
      <a
        key={i}
        href={p.href}
        target="_blank"
        rel="noreferrer"
        className="font-bold text-primary underline decoration-primary/40 hover:decoration-primary underline-offset-2"
      >
        {p.label}
      </a>
    );
  });
};

// ---------- Search / Ask mode ---------------------------------------------
const AskMode = ({ categoryId, cta = 'Ask' }) => {
  const { API } = useContext(AuthContext);
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [tierInfo, setTierInfo] = useState(null);
  const [rateLimit, setRateLimit] = useState(null);

  useEffect(() => {
    axios.get(`${API}/forum/ai-ambassador/tier-info`)
      .then((r) => setTierInfo(r.data))
      .catch(() => { /* silent — the widget still works */ });
  }, [API]);

  const ask = async () => {
    if (!q.trim()) { toast.error('Ask a question first'); return; }
    setRateLimit(null);
    setBusy(true);
    try {
      const r = await axios.post(`${API}/forum/ai-ambassador/ask`, {
        question: q.trim(),
        category_id: categoryId || null,
        include_archived: true,
      });
      setResult(r.data);
      if (r.data?.quota) {
        setTierInfo((prev) => (prev ? { ...prev, quota: r.data.quota } : prev));
      }
    } catch (e) {
      if (e.response?.status === 429) {
        setRateLimit(e.response.data?.detail || { message: 'Daily limit reached' });
      } else {
        toast.error(e.response?.data?.detail || 'AI error');
      }
    } finally { setBusy(false); }
  };

  const logClick = (topicId) => {
    if (!result?.question_id) return;
    axios.post(`${API}/forum/ai-ambassador/log-click`, {
      question_id: result.question_id,
      topic_id: topicId,
    }).catch(() => { /* best-effort */ });
  };

  const goTo = (topicId) => {
    logClick(topicId);
    navigate(`/community/t/${topicId}`);
  };

  const tierKey  = tierInfo?.tier || 'free';
  const meta     = TIER_META[tierKey] || TIER_META.free;
  const TierIcon = meta.Icon;
  const nextTier = tierInfo?.next_tier || result?.next_tier;
  const isDepthGated = tierKey === 'free' || tierKey === 'pro';
  // The typical "would benefit from a deeper answer" moment is when the free/pro
  // tier gets a non-feature-gated response — we blur the tail + show a CTA.

  return (
    <div>
      {/* Tier + quota pill */}
      {tierInfo && (
        <div className="mb-2 flex items-center justify-between gap-2 flex-wrap" data-testid="ambassador-tier-bar">
          <span className={`inline-flex items-center gap-1 rounded-full text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 border ${meta.color} ${meta.bg}`}>
            <TierIcon className="w-3 h-3" /> {meta.label} · AI support
          </span>
          {tierInfo.quota?.limit != null ? (
            <span className="text-[11px] text-muted-foreground" data-testid="ambassador-quota">
              {tierInfo.quota.remaining} / {tierInfo.quota.limit} questions left today
            </span>
          ) : (
            <span className="text-[11px] text-muted-foreground">Unlimited questions</span>
          )}
        </div>
      )}

      <Textarea
        rows={3}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={categoryId
          ? 'Ask about anything in this category — the AI searches every active + archived post.'
          : 'Ask the community anything — the AI searches every active + archived forum post.'}
        data-testid="ambassador-ask-input"
        className="text-sm"
      />
      <div className="mt-2 flex items-center justify-between gap-2">
        <AICostBadge tool="ambassador.basic_question" />
        <Button onClick={ask} disabled={busy} className="rounded-full font-bold" data-testid="ambassador-ask-btn">
          {busy ? (<><RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> Thinking…</>) : (<><Sparkles className="w-3.5 h-3.5 mr-1" /> {cta}</>)}
        </Button>
      </div>

      {rateLimit && (
        <div className="mt-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 flex items-start gap-2" data-testid="ambassador-rate-limit">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <div className="font-heading font-extrabold text-sm text-foreground">Daily limit reached</div>
            <div className="text-xs text-muted-foreground">{rateLimit.message || 'Try again after UTC midnight.'}</div>
            {rateLimit.next_tier && (
              <Button
                size="sm"
                onClick={() => navigate('/pricing')}
                className="mt-2 rounded-full h-7 text-[11px] font-bold"
                data-testid="ambassador-upgrade-quota-btn"
              >
                <Crown className="w-3 h-3 mr-1" /> Upgrade to {rateLimit.next_tier}
              </Button>
            )}
          </div>
        </div>
      )}

      {result && (
        <div
          className="mt-3 rounded-xl border border-primary/25 bg-primary/[0.04] p-3"
          data-testid="ambassador-answer"
          onClickCapture={(e) => {
            const a = e.target.closest && e.target.closest('a[href^="/community/t/"]');
            if (a) {
              const id = a.getAttribute('href').split('/').pop();
              logClick(id);
            }
          }}
        >
          {/* Feature-gated response: preview + full lock card */}
          {result.gated && result.gate_reason === 'feature' ? (
            <div data-testid="ambassador-feature-gate">
              <div className="whitespace-pre-wrap text-sm text-foreground/80 leading-relaxed">
                {renderAnswerText(result.answer)}
              </div>
              <div className="mt-3 rounded-lg border border-primary/40 bg-primary/10 p-3 flex items-start gap-3">
                <Lock className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <div className="font-heading font-extrabold text-sm">
                    {(result.required_tier || 'Higher').charAt(0).toUpperCase() + (result.required_tier || 'higher').slice(1)} plan unlocks this topic
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Detailed answers on <strong>{(result.topic || '').replace('_', ' ')}</strong> are part of the {result.required_tier || 'next'} tier — includes deeper reasoning, longer answers, and citations.
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => navigate('/pricing')}
                  className="rounded-full h-7 text-[11px] font-bold flex-shrink-0"
                  data-testid="ambassador-upgrade-btn"
                >
                  <Crown className="w-3 h-3 mr-1" /> Ask me on {result.required_tier ? result.required_tier.charAt(0).toUpperCase() + result.required_tier.slice(1) : 'Pro'}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="whitespace-pre-wrap text-sm text-foreground leading-relaxed">
                {renderAnswerText(result.answer)}
              </div>

              {/* Depth-gate blur strip — free/pro get shorter answers; nudge upsell */}
              {isDepthGated && nextTier && (
                <div className="relative mt-3" data-testid="ambassador-depth-gate">
                  {/* Blurred fake continuation to imply "there's more" */}
                  <div
                    aria-hidden="true"
                    className="rounded-lg border border-border bg-muted/60 p-3 text-sm text-muted-foreground select-none blur-[3px] pointer-events-none overflow-hidden max-h-16"
                  >
                    Further nuance: On the {nextTier.toLowerCase()} plan I would break this down step-by-step, include batch-specific tuning, and cite operator SOPs and real forum experiments — with concrete numbers you can apply immediately…
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-background/95 via-background/70 to-transparent rounded-lg">
                    <Button
                      size="sm"
                      onClick={() => navigate('/pricing')}
                      className="rounded-full h-7 text-[11px] font-bold shadow"
                      data-testid="ambassador-depth-upgrade-btn"
                    >
                      <Crown className="w-3 h-3 mr-1" /> Ask me on {nextTier} for the full answer
                    </Button>
                  </div>
                </div>
              )}

              {result.citations?.length > 0 && (
                <div className="mt-3 border-t border-border pt-3">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1.5">
                    Referenced threads
                  </div>
                  <div className="space-y-1">
                    {result.citations.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => goTo(c.id)}
                        data-testid={`ambassador-cite-${c.id}`}
                        className="w-full flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-muted transition-colors text-left"
                      >
                        <MessageSquare className="w-3 h-3 text-primary flex-shrink-0" />
                        <span className="text-xs font-semibold text-foreground truncate flex-1">{c.title}</span>
                        {c.is_archived && (
                          <span className="text-[9px] uppercase tracking-widest font-bold bg-slate-500/15 text-slate-600 rounded px-1.5 py-0.5">Archived</span>
                        )}
                        <ChevronRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ---------- Fact-check mode -----------------------------------------------
const FactCheckMode = ({ topicId }) => {
  const { API } = useContext(AuthContext);
  const [busy, setBusy] = useState(false);
  const [fc, setFc] = useState(null);

  const run = async (force = false) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/forum/ai-ambassador/fact-check/${topicId}`, { force_refresh: force });
      setFc(r.data.fact_check);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fact-check unavailable');
    } finally { setBusy(false); }
  };

  const meta = fc ? (VERDICT_META[fc.verdict] || VERDICT_META.unverified) : null;

  return (
    <div>
      {!fc && (
        <div className="text-sm text-muted-foreground">
          Run a fact-check to have the AI compare this thread against Freeze Dry Academy&apos;s reference knowledge.
        </div>
      )}
      {fc && (
        <div className={`rounded-xl border ${meta.border} ${meta.bg} p-3`} data-testid="ambassador-factcheck">
          <div className="flex items-center gap-2 mb-2">
            <meta.Icon className={`w-4 h-4 ${meta.color}`} />
            <span className={`text-[10px] uppercase tracking-widest font-bold ${meta.color}`}>{meta.label}</span>
            <span className="ml-auto text-[10px] font-bold text-muted-foreground">
              confidence {Math.round((fc.confidence || 0) * 100)}%
            </span>
          </div>
          <div className="text-sm text-foreground leading-relaxed">{fc.summary}</div>
          {(fc.key_points?.length > 0) && (
            <div className="mt-2">
              <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Main claims</div>
              <ul className="list-disc pl-4 space-y-0.5 text-xs text-foreground/85">
                {fc.key_points.map((p, i) => <li key={i}>{p}</li>)}
              </ul>
            </div>
          )}
          {(fc.concerns?.length > 0) && (
            <div className="mt-2">
              <div className={`text-[10px] uppercase tracking-widest font-bold ${meta.color} mb-1`}>Concerns</div>
              <ul className="list-disc pl-4 space-y-0.5 text-xs text-foreground/85">
                {fc.concerns.map((p, i) => <li key={i}>{p}</li>)}
              </ul>
            </div>
          )}
          {(fc.recommendations?.length > 0) && (
            <div className="mt-2">
              <div className="text-[10px] uppercase tracking-widest font-bold text-primary mb-1">Recommendations</div>
              <ul className="list-disc pl-4 space-y-0.5 text-xs text-foreground/85">
                {fc.recommendations.map((p, i) => <li key={i}>{p}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
      <div className="mt-2 flex items-center justify-end gap-2">
        <Button size="sm" variant="outline" onClick={() => run(true)} disabled={busy} className="rounded-full text-xs" data-testid="ambassador-refresh-btn">
          <RefreshCw className={`w-3 h-3 mr-1 ${busy ? 'animate-spin' : ''}`} /> {fc ? 'Re-check' : ''}
        </Button>
        {!fc && (
          <Button onClick={() => run(false)} disabled={busy} className="rounded-full font-bold text-xs" data-testid="ambassador-run-btn">
            <Sparkles className="w-3.5 h-3.5 mr-1" /> {busy ? 'Analysing…' : 'Run fact-check'}
          </Button>
        )}
      </div>
    </div>
  );
};

// ---------- Container -----------------------------------------------------
const AIAmbassadorPanel = ({
  mode = 'search',
  categoryId = null,
  topicId = null,
  title,
  subtitle,
  cta,
}) => {
  const [open, setOpen] = useState(false);
  const resolvedTitle = title || (mode === 'fact-check' ? 'Fact Check with AI' : 'AI Summarize and Research');
  const resolvedSubtitle = subtitle || (mode === 'fact-check'
    ? 'Check this thread against Freeze Dry Academy knowledge.'
    : 'Search + summarize every post, live or archived.');
  const resolvedCta = cta || (mode === 'search' ? 'Summarize & Research' : undefined);
  return (
    <section className="pro-card p-4" data-testid={`ambassador-panel-${mode}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 text-left"
        data-testid="ambassador-toggle"
      >
        <div className="h-8 w-8 rounded-full flex items-center justify-center bg-primary/15 text-primary flex-shrink-0">
          <Bot className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-heading text-sm font-extrabold text-foreground">{resolvedTitle}</div>
          <div className="text-[11px] text-muted-foreground truncate">{resolvedSubtitle}</div>
        </div>
        <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="mt-3">
          {mode === 'fact-check'
            ? <FactCheckMode topicId={topicId} />
            : <AskMode categoryId={categoryId} cta={resolvedCta} />}
        </div>
      )}
    </section>
  );
};

export default AIAmbassadorPanel;
